import React, { useState } from 'react';
import Button from '@sats-group/fresh-fitness-ui-lib/react/button';
import Link from '@sats-group/fresh-fitness-ui-lib/react/link';
import Message from '@sats-group/fresh-fitness-ui-lib/react/message';
import Select from '@sats-group/fresh-fitness-ui-lib/react/select';
import Text from '@sats-group/fresh-fitness-ui-lib/react/text';

import Attachment from 'root/client/components/attachment/attachment';
import ContentContainer from 'root/client/components/content-container/content-container';
import HiddenInput from 'root/client/components/hidden-input/hidden-input';
import Layout from 'root/client/components/layout/layout';
import TextArea from 'root/client/components/text-area/text-area';
import TextField from 'root/client/components/text-field/text-field';
import useIsMounted from 'root/client/hooks/use-is-mounted';

import type { MemberCareFormPage as Props } from './member-care-form-page.types';

const MemberCareFormPage: React.FunctionComponent<Props> = ({
  attachment,
  category,
  countryIsoCode,
  description,
  endpoint,
  errorMessage,
  errorRedirectUrl,
  help,
  layout,
  nudge,
  previousPage,
  submit,
  successMessage,
  successRedirectUrl,
  textInputs = [],
  title,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMounted = useIsMounted();

  const handleSubmit: React.SubmitEventHandler = event => {
    if (!(event.target as HTMLFormElement).checkValidity()) {
      event.preventDefault();
      return;
    }

    setIsSubmitting(true);
  };

  return (
    <Layout
      {...layout}
      theme={successMessage ? Layout.themes.Primary : undefined}
    >
      {successMessage ? (
        <ContentContainer width={ContentContainer.widths.Narrow}>
          <div className="member-care-form-page__success">
            <Message
              key={successMessage.title}
              type={Message.types.success}
              {...successMessage}
            />
          </div>
        </ContentContainer>
      ) : (
        <div className="member-care-form-page__main">
          <ContentContainer
            className="member-care-form-page__content"
            width={ContentContainer.widths.Medium}
          >
            {previousPage ? (
              <div className="member-care-form-page__previous-page">
                <Link {...previousPage} />
              </div>
            ) : null}
            <Text
              elementName="h1"
              size={Text.sizes.header2}
              variant={Text.variants.hero}
            >
              {title}
            </Text>
            <div className="member-care-form-page__nudge">
              <Message
                header={nudge.title}
                link={{ href: nudge.link.href, label: nudge.link.text }}
                text={nudge.text}
                type={Message.types.success}
              />
            </div>
            <form
              className="member-care-form-page"
              action={endpoint}
              method="POST"
              encType="multipart/form-data"
              onSubmit={handleSubmit}
            >
              <HiddenInput {...countryIsoCode} />
              {isMounted ? (
                <>
                  <HiddenInput
                    name="redirect-success"
                    value={`${window.location.origin}${successRedirectUrl}`}
                  />
                  <HiddenInput
                    name="redirect-error"
                    value={`${window.location.origin}${errorRedirectUrl}`}
                  />
                </>
              ) : null}
              {errorMessage ? (
                <div className="member-care-form-page__error-message">
                  <Message key={errorMessage.text} {...errorMessage} />
                </div>
              ) : null}
              <div className="member-care-form-page__fields">
                <div className="member-care-form-page__help">
                  <Text>{help}</Text>
                </div>
                <div className="member-care-form-page__inputs">
                  {textInputs.map(item => (
                    <div
                      key={item.name}
                      className="member-care-form-page__input"
                    >
                      <TextField {...item} />
                    </div>
                  ))}
                </div>
                {category ? (
                  <div className="member-care-form-page__category">
                    <Select {...category} />
                  </div>
                ) : null}
                <div className="member-care-form-page__description">
                  <TextArea {...description} rows={6} />
                </div>
              </div>
              {attachment ? (
                <div className="member-care-form-page__attachment">
                  <Attachment {...attachment} />
                </div>
              ) : null}
              <div className="member-care-form-page__submit">
                <Button
                  data-test-submit
                  type="submit"
                  disabled={isSubmitting}
                  text={submit}
                />
              </div>
            </form>
          </ContentContainer>
        </div>
      )}
    </Layout>
  );
};

export default MemberCareFormPage;
